import { ChevronsLeft, Loader2 } from 'lucide-react';
import { useMemo } from 'react';

import { AtlasIcon, OrgLogoId, OrgLogos } from '@crossnokaye/ux-animatics';
import {
  AccessSelector,
  cn,
  HeaderWithIcon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@crossnokaye/ux-common';

import { NavOptions } from '@/components/navigation/constants';
import { AccessSelectorContent } from '@/components/navigation/left/components/shared/access-selector-content';
import { SquareButton } from '@/components/navigation/left/components/shared/square-button';
import { useAgentProxyMode } from '@/hooks/use-agent-proxy-mode';
import { useNavStore } from '@/stores/nav';

import { ExpandedMainNav } from './expanded-main-nav';

export const ExpandedNav = ({
  onSelect,
  onSignOut,
  onToggleActive,
  switchToFacilityMenu,
  toggleExpanded,
  isViewOnly,
}: {
  onSelect: (id: string) => void;
  onSignOut: () => void;
  onToggleActive: () => void;
  switchToFacilityMenu: () => void;
  toggleExpanded: () => void;
  isViewOnly: boolean;
}) => {
  const {
    userName,
    facilityName,
    orgName,
    roles,
    orgIcon,
    enableFacilityMenu,
    userAccessIsLoading,
    activeNavMenu,
  } = useNavStore();

  const { isAgentProxyMode } = useAgentProxyMode();

  const handleSignOut = () => {
    if (!onSignOut) return;
    onSignOut();
  };

  const handleToggleActive = () => {
    if (!onToggleActive) return;
    onToggleActive();
  };

  // alphabetically sort roles, because the order of roles in the database is not guaranteed
  // and our roles happen to have alphabetical prevalence.  Ex. Admin > Blast > Viewer
  const topRole = useMemo(() => {
    return roles && roles.length ? [...roles].sort((a, b) => a.localeCompare(b))[0] : '';
  }, [roles]);

  const isLoading = !userName || !topRole || userAccessIsLoading;

  const OrgLogoIcon = OrgLogos[orgIcon as OrgLogoId];

  return (
    <TooltipProvider>
      <div className={'h-full flex flex-col'} data-testid={'expanded-nav'}>
        <div className={'flex-shrink-0'}>
          <div
            className={'flex justify-between items-center py-4 px-3 cursor-pointer'}
            onClick={toggleExpanded}
            data-testid={'expanded-nav-logo'}
          >
            <AtlasIcon symbol={'atlasLogoFull'} width={'10rem'} height={'1.5rem'} />
            <Tooltip>
              <TooltipTrigger asChild>
                <SquareButton
                  icon={<ChevronsLeft size={'1rem'} />}
                  onClick={toggleExpanded}
                  data-testid={'expanded-nav-collapse-toggle'}
                />
              </TooltipTrigger>
              <TooltipContent align={'center'} side={'right'} alignOffset={-10}>
                <span className={'text-xs'}>{'Click to Collapse Menu'}</span>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className={'mb-5'}>
            {orgIcon && orgName && facilityName ? (
              <Tooltip>
                <TooltipTrigger disabled={!enableFacilityMenu} className={'w-full'} asChild>
                  <HeaderWithIcon
                    title={facilityName}
                    icon={
                      OrgLogoIcon ? (
                        <OrgLogoIcon
                          size={'2rem'}
                          className={'flex justify-center align-middle'}
                          color={'white'}
                        />
                      ) : null
                    }
                    subtitle={orgName}
                    onClick={enableFacilityMenu ? switchToFacilityMenu : undefined}
                    className={cn(
                      'text-foreground',
                      enableFacilityMenu && 'cursor-pointer hover:bg-tertiary'
                    )}
                    data-testid={'expanded-nav-org-header'}
                  />
                </TooltipTrigger>
                <TooltipContent align={'center'} side={'right'}>
                  <span className={'text-xs'}>{'Click to View Facility Picker'}</span>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Loader2 className={'animate-spin'} />
            )}
          </div>
        </div>
        {activeNavMenu === NavOptions.Main ? <ExpandedMainNav onSelect={onSelect} /> : null}
        {!isAgentProxyMode && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={'flex-shrink-0 mt-auto border-t border-border bg-secondary'}>
                <AccessSelector
                  name={userName ?? ''}
                  status={isViewOnly ? 'view only' : 'active'}
                  role={topRole}
                  className={'bg-muted border-border !border-t-1 hover:bg-tertiary rounded-sm'}
                  loading={isLoading}
                  data-testid={'expanded-nav-access-selector'}
                >
                  <AccessSelectorContent
                    isViewOnly={isViewOnly}
                    handleToggleActive={handleToggleActive}
                    handleSignOut={handleSignOut}
                  />
                </AccessSelector>
              </div>
            </TooltipTrigger>
            <TooltipContent align={'start'} side={'right'}>
              <span className={'text-xs'}>{'Click to View Access Options'}</span>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};
